/**
 * Custom hook for managing content summarization functionality
 */

import { useState } from 'react'
import { api } from '../../../../services'
import { message } from 'antd'
import { useTranslation } from 'react-i18next'

export const useSummarizeContent = ({ language }) => {
  const [summarizeModal, setSummarizeModal] = useState({
    isVisible: false,
    instructions: '',
    isSummarizing: false
  })

  const [summarizedContent, setSummarizedContent] = useState('')
  const [isApplyingAll, setIsApplyingAll] = useState(false)
  const [previewAllModal, setPreviewAllModal] = useState({
    isVisible: false,
    stagedContent: null
  })
  const [isApplyAllConfirmVisible, setIsApplyAllConfirmVisible] = useState(
    false
  )

  const { t } = useTranslation()

  const handleSummarizeContent = () => {
    setSummarizeModal({
      isVisible: true,
      instructions: '',
      isSummarizing: false
    })
  }

  const handleSummarizeSubmit = generatedContentModal => {
    const { content: generatedContent, title, lesson } = generatedContentModal
    const { instructions } = summarizeModal

    if (!instructions.trim()) {
      message.error(t('v5:please_enter_editing_instruction'))
      return
    }

    setSummarizeModal(prev => ({
      ...prev,
      isSummarizing: true
    }))

    // Extract image paths if they exist in the content
    const imgPathRegex = /https:\/\/[^\s]+\.(jpg|jpeg|png|gif|webp)/gi
    const imgPaths = generatedContent.match(imgPathRegex) || []

    const payload = {
      content: generatedContent,
      user_instructions: instructions,
      img_path: imgPaths,
      language
    }

    api.courses
      .summarizeCourseContent(payload)
      .then(res => {
        if (res.data && res.data.content) {
          setSummarizedContent(res.data.content)
          message.success(t('v5:content_edited_successfully'))
        } else {
          message.error(t('vv5:failed_to_edit_content'))
        }
      })
      .catch(err => {
        console.error('Summarize error:', err)
        message.error(t('vv5:failed_to_edit_content'))
      })
      .finally(() => {
        setSummarizeModal(prev => ({
          ...prev,
          isSummarizing: false
        }))
      })
  }

  const handleUseSummarizedContent = setGeneratedContentModal => {
    setGeneratedContentModal(prev => ({
      ...prev,
      content: summarizedContent
    }))
    setSummarizeModal({
      isVisible: false,
      instructions: '',
      isSummarizing: false
    })
    setSummarizedContent('')
    message.success('Edited content applied!')
  }

  const handleApplyToAll = content => {
    const { instructions } = summarizeModal
    if (!instructions || instructions.trim().length === 0) {
      message.error(t('v5:please_enter_editing_instruction'))
      return
    }

    setIsApplyingAll(true)
    const payload = { bulk_update: true, content: content, user_instructions: instructions, language }
    api.courses
      .summarizeWholeCourseContent(payload)
      .then(res => {
        const data = res && res.data
        const returned = data && (data.content ? data.content : data)
        if (returned && typeof returned === 'object') {
          setPreviewAllModal({ isVisible: true, stagedContent: returned })
        } else {
          message.error('Unexpected response for apply all')
        }
      })
      .catch(() => {
        message.error('Failed to apply edits to all topics')
      })
      .finally(() => {
        setIsApplyingAll(false)
      })
  }

  const handleApplyAllPreview = (
    updateContent,
    saveContentToDatabase,
    setGeneratedContentModal
  ) => {
    // Commit the staged changes to content
    if (!previewAllModal.stagedContent) return
    const updated = previewAllModal.stagedContent

    // Update content state - updateContent already calls setContent internally
    updateContent(updated)
    saveContentToDatabase(updated)

    // Close all modals when apply to all is successful
    setPreviewAllModal({ isVisible: false, stagedContent: null })
    setSummarizeModal({
      isVisible: false,
      instructions: '',
      isSummarizing: false
    })
    setGeneratedContentModal({
      isVisible: false,
      content: '',
      title: '',
      lesson: ''
    })
    setIsApplyAllConfirmVisible(false)
    setSummarizedContent('')

    message.success('All changes applied')
  }

  const handleCancelSummarize = () => {
    setSummarizeModal({
      isVisible: false,
      instructions: '',
      isSummarizing: false
    })
    setSummarizedContent('')
  }

  const handleApplyAllFromSummarized = (content, generatedContentModal) => {
    const { instructions } = summarizeModal
    if (!instructions || instructions.trim().length === 0) {
      message.error(t('v5:please_enter_editing_instruction'))
      return
    }

    setIsApplyingAll(true)

    // Remove the current topic from the content payload
    const { lesson: currentLesson, title: currentTitle } = generatedContentModal
    const contentWithoutCurrentTopic = { ...content }

    if (currentLesson && currentTitle && contentWithoutCurrentTopic[currentLesson]) {
      delete contentWithoutCurrentTopic[currentLesson][currentTitle]

      // If the lesson becomes empty, remove it entirely
      if (Object.keys(contentWithoutCurrentTopic[currentLesson]).length === 0) {
        delete contentWithoutCurrentTopic[currentLesson]
      }
    }

    const payload = { bulk_update: true, content: contentWithoutCurrentTopic, user_instructions: instructions, language }
    api.courses
      .summarizeWholeCourseContent(payload)
      .then(res => {
        const data = res && res.data
        const returned = data && (data.content ? data.content : data)
        if (returned && typeof returned === 'object') {
          // Combine the API response with the already generated content
          const combinedContent = { ...returned }

          // Add back the current topic with its summarized content
          if (currentLesson && currentTitle) {
            if (!combinedContent[currentLesson]) {
              combinedContent[currentLesson] = {}
            }
            combinedContent[currentLesson][currentTitle] = {
              generatedContent: summarizedContent
            }
          }

          setPreviewAllModal({ isVisible: true, stagedContent: combinedContent })
          setSummarizedContent('') // Close the summarized content modal
        } else {
          message.error('Unexpected response for apply all')
        }
      })
      .catch(() => {
        message.error('Failed to apply edits to all topics')
      })
      .finally(() => {
        setIsApplyingAll(false)
      })
  }

  return {
    summarizeModal,
    setSummarizeModal,
    summarizedContent,
    setSummarizedContent,
    isApplyingAll,
    setIsApplyingAll,
    previewAllModal,
    setPreviewAllModal,
    isApplyAllConfirmVisible,
    setIsApplyAllConfirmVisible,
    handleSummarizeContent,
    handleSummarizeSubmit,
    handleUseSummarizedContent,
    handleApplyToAll,
    handleApplyAllPreview,
    handleCancelSummarize,
    handleApplyAllFromSummarized
  }
}
